// src/pages/admin/AdminTeam.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

const BUCKET = "doctors";
const DAYS = ["一", "二", "三", "四", "五", "六"];
const SLOTS = [
  { key: "morning", label: "早" },
  { key: "afternoon", label: "午" },
  { key: "evening", label: "晚" },
];

const emptySchedule = () =>
  DAYS.map((day) => ({ day, morning: "", afternoon: "", evening: "" }));

const linesToArray = (s) =>
  (s || "")
    .split(/\r?\n/)
    .map((t) => t.trim())
    .filter(Boolean);

const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

export default function AdminTeam() {
  // 列表
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(null); // success | error | info

  // 表單（新增/編輯共用）
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [specLines, setSpecLines] = useState(""); // 專長（多行）
  const [eduLines, setEduLines] = useState(""); // 學歷（多行）
  const [expLines, setExpLines] = useState(""); // 經歷（多行）
  const [schedule, setSchedule] = useState(emptySchedule());
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState("");
  const [file, setFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");

  const styles = useMemo(
    () => ({
      wrap: { maxWidth: 1080, margin: "24px auto", padding: "0 16px" },
      h2: { margin: "0 0 12px", fontSize: 22, fontWeight: 800 },
      grid: { display: "grid", gap: 12 },
      card: {
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        padding: 16,
      },
      row: { display: "grid", gap: 8 },
      two: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" },
      label: { fontSize: 13, color: "#374151", fontWeight: 600 },
      input: {
        height: 40,
        border: "1px solid #d1d5db",
        borderRadius: 10,
        padding: "0 10px",
        outline: "none",
      },
      ta: {
        minHeight: 90,
        border: "1px solid #d1d5db",
        borderRadius: 10,
        padding: 10,
        outline: "none",
        resize: "vertical",
      },
      tableWrap: { overflowX: "auto" },
      table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        border: "1px solid #e5e7eb",
        borderRadius: 8,
      },
      th: {
        textAlign: "center",
        background: "#f1f5f9",
        borderBottom: "1px solid #e5e7eb",
        padding: "8px",
      },
      td: {
        textAlign: "center",
        borderBottom: "1px solid #f8fafc",
        padding: 8,
      },
      btnPri: {
        height: 40,
        padding: "0 14px",
        borderRadius: 10,
        border: "1px solid #0ea5e9",
        background: "#0ea5e9",
        color: "#fff",
        fontWeight: 700,
        cursor: "pointer",
      },
      btnSec: {
        height: 36,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid #64748b",
        background: "#64748b",
        color: "#fff",
        cursor: "pointer",
      },
      btnWarn: {
        height: 36,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid #ef4444",
        background: "#ef4444",
        color: "#fff",
        cursor: "pointer",
      },
      listRow: {
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 12,
        alignItems: "center",
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
      },
      avatar: {
        width: 56,
        height: 56,
        borderRadius: 8,
        objectFit: "cover",
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
      },
      msg: (t) => ({
        marginTop: 10,
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid",
        ...(t === "success"
          ? { background: "#ecfdf5", borderColor: "#6ee7b7", color: "#065f46" }
          : t === "error"
          ? { background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }
          : {
              background: "#f1f5f9",
              borderColor: "#e2e8f0",
              color: "#334155",
            }),
      }),
      actionsLine: { display: "flex", gap: 8, alignItems: "center" },
    }),
    []
  );

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setBrief("");
    setSpecLines("");
    setEduLines("");
    setExpLines("");
    setSchedule(emptySchedule());
    setIsActive(true);
    setSortOrder("");
    setFile(null);
    setPhotoUrl("");
  };

  const fetchRows = async () => {
    setLoading(true);
    setMsg("");
    setMsgType(null);
    const { data, error } = await supabase
      .from("doctors")
      .select("*")
      .order("sort_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });
    if (error) {
      console.error(error);
      setMsg("讀取失敗");
      setMsgType("error");
    } else {
      setRows(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const uploadPhotoIfAny = async () => {
    if (!file) return photoUrl || null;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `d_${Date.now()}.${ext}`;
    const up = await supabase.storage.from(BUCKET).upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });
    if (up.error) throw up.error;
    const pub = supabase.storage.from(BUCKET).getPublicUrl(path);
    return pub.data.publicUrl;
  };

  const onSubmit = async () => {
    if (!name.trim()) {
      setMsg("請填寫醫師姓名");
      setMsgType("info");
      return;
    }
    setLoading(true);
    setMsg("");
    setMsgType(null);
    try {
      const image_url = await uploadPhotoIfAny();
      const payload = {
        name: name.trim(),
        brief: brief.trim() || null,
        specialties: linesToArray(specLines),
        education: linesToArray(eduLines),
        experience: linesToArray(expLines),
        schedule, // [{day:'一', morning:'●'|'' ...}]
        is_active: !!isActive,
        sort_order:
          String(sortOrder).trim() === "" ? null : Number(sortOrder) || null,
        photo_url: image_url,
      };

      if (editingId) {
        const { error } = await supabase
          .from("doctors")
          .update(payload)
          .eq("id", editingId);
        if (error) throw error;
        setMsg("✅ 已更新醫師資料");
        setMsgType("success");
      } else {
        const { error } = await supabase.from("doctors").insert(payload);
        if (error) throw error;
        setMsg("✅ 已新增醫師");
        setMsgType("success");
      }
      resetForm();
      fetchRows();
    } catch (err) {
      console.error(err);
      setMsg("儲存失敗");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setName(row.name || "");
    setBrief(row.brief || "");
    setSpecLines(arrayToLines(row.specialties));
    setEduLines(arrayToLines(row.education));
    setExpLines(arrayToLines(row.experience));
    setSchedule(
      Array.isArray(row.schedule) && row.schedule.length === DAYS.length
        ? row.schedule
        : emptySchedule()
    );
    setIsActive(!!row.is_active);
    setSortOrder(row.sort_order ?? "");
    setPhotoUrl(row.photo_url || "");
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (row) => {
    if (!window.confirm(`確定刪除「${row.name}」嗎？`)) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from("doctors")
        .delete()
        .eq("id", row.id);
      if (error) throw error;
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setMsg("✅ 已刪除");
      setMsgType("success");
    } catch (err) {
      console.error(err);
      setMsg("刪除失敗");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const onToggleActive = async (row) => {
    const { error } = await supabase
      .from("doctors")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) {
      console.error(error);
      setMsg("切換顯示失敗");
      setMsgType("error");
    } else {
      setRows((rs) =>
        rs.map((r) => (r.id === row.id ? { ...r, is_active: !r.is_active } : r))
      );
      setMsg("✅ 已更新狀態");
      setMsgType("success");
    }
  };

  const onToggleSchedule = (i, k) => {
    setSchedule((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [k]: r[k] ? "" : "●" } : r))
    );
  };

  return (
    <section style={styles.wrap}>
      <h2 style={styles.h2}>醫師卡片管理</h2>

      {/* 建立 / 編輯 */}
      <div style={styles.card}>
        <div className="grid" style={styles.grid}>
          <div style={styles.row}>
            <label style={styles.label}>姓名*</label>
            <input
              style={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例：李啟鐘 醫師"
              disabled={loading}
            />
          </div>

          <div style={styles.row}>
            <label style={styles.label}>簡介（卡片副標）</label>
            <input
              style={styles.input}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="例：耳鼻喉科、氣喘、過敏"
              disabled={loading}
            />
          </div>

          <div style={styles.two}>
            <div style={styles.row}>
              <label style={styles.label}>專長（每行一項）</label>
              <textarea
                style={styles.ta}
                value={specLines}
                onChange={(e) => setSpecLines(e.target.value)}
                placeholder="過敏\n氣喘控制\n耳鼻喉急症"
                disabled={loading}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>學歷（每行一項）</label>
              <textarea
                style={styles.ta}
                value={eduLines}
                onChange={(e) => setEduLines(e.target.value)}
                placeholder="台大耳鼻喉部住院醫師\n台大耳鼻喉部總醫師"
                disabled={loading}
              />
            </div>
          </div>

          <div style={styles.row}>
            <label style={styles.label}>經歷（每行一項）</label>
            <textarea
              style={styles.ta}
              value={expLines}
              onChange={(e) => setExpLines(e.target.value)}
              placeholder="部立桃園醫院主治醫師\n耳鼻喉科專科醫師"
              disabled={loading}
            />
          </div>

          <div style={styles.two}>
            <div style={styles.row}>
              <label style={styles.label}>頭像上傳（可選）</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={loading}
              />
              {photoUrl && (
                <img
                  alt="預覽"
                  src={photoUrl}
                  style={{
                    marginTop: 8,
                    width: 120,
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />
              )}
            </div>
            <div style={styles.row}>
              <label style={styles.label}>排序（數字小在前）</label>
              <input
                style={styles.input}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                placeholder="例：10"
                disabled={loading}
              />
              <label style={styles.label}>是否顯示於前台</label>
              <select
                style={styles.input}
                value={isActive ? "1" : "0"}
                onChange={(e) => setIsActive(e.target.value === "1")}
                disabled={loading}
              >
                <option value="1">顯示</option>
                <option value="0">隱藏</option>
              </select>
            </div>
          </div>

          <div style={styles.row}>
            <label style={styles.label}>每週時段</label>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>星期</th>
                    {SLOTS.map((s) => (
                      <th key={s.key} style={styles.th}>
                        {s.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((r, i) => (
                    <tr key={r.day}>
                      <td style={styles.td}>{r.day}</td>
                      {SLOTS.map((s) => (
                        <td key={s.key} style={styles.td}>
                          <input
                            type="checkbox"
                            checked={!!r[s.key]}
                            onChange={() => onToggleSchedule(i, s.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button style={styles.btnPri} onClick={onSubmit} disabled={loading}>
              {loading ? "處理中…" : editingId ? "更新醫師" : "新增醫師"}
            </button>
            {editingId && (
              <button
                style={styles.btnSec}
                onClick={resetForm}
                disabled={loading}
              >
                取消編輯
              </button>
            )}
          </div>

          {msg && <div style={styles.msg(msgType)}>{msg}</div>}
        </div>
      </div>

      {/* 列表 */}
      <div style={{ ...styles.card, marginTop: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>目前醫師</div>
        {rows.length === 0 ? (
          <div style={{ color: "#6b7280" }}>
            {loading ? "讀取中…" : "尚無資料"}
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} style={styles.listRow}>
              <img
                alt="avatar"
                src={r.photo_url || "/doctors/placeholder.png"}
                style={styles.avatar}
                onError={(e) =>
                  (e.currentTarget.src = "/doctors/placeholder.png")
                }
              />
              <div>
                <div style={{ fontWeight: 700 }}>
                  {r.name}{" "}
                  <span
                    style={{
                      fontSize: 12,
                      color: r.is_active ? "#166534" : "#991b1b",
                    }}
                  >
                    {r.is_active ? "（顯示中）" : "（已隱藏）"}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>
                  {r.brief || "—"}
                </div>
              </div>
              <div style={styles.actionsLine}>
                <button style={styles.btnSec} onClick={() => onEdit(r)}>
                  編輯
                </button>
                <button style={styles.btnSec} onClick={() => onToggleActive(r)}>
                  {r.is_active ? "設為隱藏" : "設為顯示"}
                </button>
                <button style={styles.btnWarn} onClick={() => onDelete(r)}>
                  刪除
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
